"use client"

import { useEffect, useMemo, useState } from "react"
import { createRoute } from "@tanstack/react-router"

import { AddCandidateDialog } from "@/components/candidates/add-candidate-dialog"
import {
  GraphiteCandidates,
  type CandidateRow,
} from "@/components/candidates/graphite-candidates"
import { CandidateProfileOverlayContainer } from "@/components/candidate-profile-overlay"
import { useTasksComments } from "@/contexts/tasks-comments-context"
import { useCandidateOverlayUrlState } from "@/hooks/use-candidate-overlay"
import { useDebounce } from "@/hooks/useDebounce"
import { asArray } from "@/lib/as-array"
import { STAGE_ORDER } from "@/lib/stage-colors"
import MainAppRoute from "@/rootRoutes/MainApp"
import { ValidateSearch } from "@/utils/candidate-profile-overlay-utils"
import { useGetCandidates, useGetCandidatesSearch } from "openapi/api/endpoints"
import { GetCandidates200CandidatesItem } from "openapi/api/model"

const CandidatesRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/candidates",
  validateSearch: ValidateSearch,
  component: CandidatesComponent,
})
export default CandidatesRoute

type ApiCandidate = GetCandidates200CandidatesItem

type CandidateRawJson = NonNullable<ApiCandidate["raw_json"]> & {
  location?: string | null
}

function initialsOf(fullName: string) {
  const names = fullName.trim().split(/\s+/).filter(Boolean)
  if (names.length === 0) return "?"
  if (names.length === 1) return names[0].slice(0, 2).toUpperCase()
  return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
}

/** 5a writes elapsed time in language: "5 minutes ago", "Yesterday". */
function relativeTime(iso?: string) {
  if (!iso) return "—"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "—"

  const minutes = Math.floor((Date.now() - then) / 60000)
  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return "Yesterday"
  if (days < 7) return `${days} days ago`

  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`

  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`
  return new Date(then).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

/**
 * Total experience in whole years, merging overlapping roles so concurrent
 * positions are not counted twice. Mono in the table, because it is a machine
 * value.
 */
function yearsOfExperience(experiences?: ApiCandidate["experiences"]) {
  if (!experiences?.length) return "0y"

  const ranges: { start: number; end: number }[] = []
  experiences.forEach((experience) => {
    if (!experience?.starts_at) return
    const start = new Date(experience.starts_at).getTime()
    const end = experience.ends_at ? new Date(experience.ends_at).getTime() : Date.now()
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return
    ranges.push({ start, end })
  })

  if (ranges.length === 0) return "0y"
  ranges.sort((a, b) => a.start - b.start)

  const merged: { start: number; end: number }[] = []
  let current = ranges[0]
  for (const range of ranges.slice(1)) {
    if (range.start <= current.end) {
      current = { start: current.start, end: Math.max(current.end, range.end) }
    } else {
      merged.push(current)
      current = range
    }
  }
  merged.push(current)

  const YEAR = 365.25 * 24 * 60 * 60 * 1000
  const total = merged.reduce((sum, range) => sum + (range.end - range.start), 0)
  const years = Math.floor(total / YEAR)
  return years < 1 ? "<1y" : `${years}y`
}

function CandidatesComponent() {
  const [searchQuery, setSearchQuery] = useState("")
  const [effectiveSearchQuery, setEffectiveSearchQuery] = useState("")
  const debouncedQuery = useDebounce(searchQuery, 400)

  const navigate = CandidatesRoute.useNavigate()
  const search = CandidatesRoute.useSearch()
  const overlay = useCandidateOverlayUrlState({
    rawSearch: search,
    setSearch: (updater) => navigate({ search: updater }),
  })

  const { getCommentsForCandidate } = useTasksComments()

  const {
    data: candidatesResponse,
    isLoading,
    error,
  } = useGetCandidates({ search_str: effectiveSearchQuery || undefined })

  /*
    Stage is not on the candidate record — it lives on the job–candidate link,
    because a candidate can sit at a different stage on every job they are on.
    5a still shows one Stage per row, so this reads the links and takes the
    furthest-along stage that candidate has reached anywhere.
  */
  /*
    No `limit` here, deliberately. The endpoint validates it with
    `z.number()` against `req.query`, where every value is a string, so
    *any* limit is rejected with a 400 — passing one silently emptied this map
    and every row showed an em dash. Until the backend coerces it, the server
    default of 10 applies and stages are only resolved for the ten most
    recently created links.
  */
  const { data: links } = useGetCandidatesSearch()

  /*
    One entry per candidate: the furthest-along link they hold anywhere, with
    the job it belongs to. The stage feeds 5a's Stage column; the job feeds the
    Outreach tab, which cannot generate without one. Both come from the same
    link so the copy is written against the job whose stage the row shows.
  */
  const linkByCandidate = useMemo(() => {
    const held = new Map<string, { stage: string; jobId?: string; jobTitle?: string }>()
    // `?? []` only guards null; a non-array body walks straight into `for…of`
    // and takes the page down. Same guard as everywhere else.
    for (const link of asArray<NonNullable<typeof links>[number]>(links)) {
      const id = link.candidates?.id
      if (!id || !link.stage) continue
      const current = held.get(id)
      if (
        !current ||
        STAGE_ORDER.indexOf(link.stage) > STAGE_ORDER.indexOf(current.stage as never)
      ) {
        held.set(id, {
          stage: link.stage,
          jobId: link.jobs?.id,
          // 14a names the job in a comment's provenance; the link already has it.
          jobTitle: link.jobs?.title,
        })
      }
    }
    return held
  }, [links])

  useEffect(() => {
    setEffectiveSearchQuery(debouncedQuery)
  }, [debouncedQuery])

  /*
    5a's count line reads "248 candidates · 13 shown" — the left number is how
    many exist, the right is how many survived the filters. Search is server
    side, so the response shrinks with the query and cannot supply the left
    number; the endpoint returns no total either. Remember the size of the last
    unsearched response instead, which is the database count until a candidate
    is added or removed.
  */
  const [databaseTotal, setDatabaseTotal] = useState(0)
  useEffect(() => {
    if (effectiveSearchQuery || !candidatesResponse) return
    setDatabaseTotal(candidatesResponse.candidates?.length ?? 0)
  }, [effectiveSearchQuery, candidatesResponse])

  const rows = useMemo<CandidateRow[]>(() => {
    return (candidatesResponse?.candidates ?? [])
      .filter((candidate) => Boolean(candidate.candidate_id))
      .map((candidate) => {
        const raw = (candidate.raw_json ?? {}) as CandidateRawJson
        const id = candidate.candidate_id as string
        const name = candidate.full_name ?? raw.full_name ?? "Unnamed candidate"
        const company = candidate.experiences?.[0]?.company ?? ""
        const role = candidate.current_role_title ?? candidate.headline ?? ""

        return {
          id,
          name,
          initials: initialsOf(name),
          role: [role, company].filter(Boolean).join(" · ") || "—",
          location: raw.location_str ?? candidate.location ?? raw.location ?? "—",
          experience: yearsOfExperience(candidate.experiences),
          stage: linkByCandidate.get(id)?.stage,
          comments: getCommentsForCandidate(id).length,
          added: relativeTime(candidate.inserted_at),
        }
      })
  }, [candidatesResponse, getCommentsForCandidate, linkByCandidate])

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-[13px] text-destructive">
          Error loading candidates: {error.message}
        </p>
      </div>
    )
  }

  return (
    <>
      <GraphiteCandidates
        rows={rows}
        total={databaseTotal || rows.length}
        isLoading={isLoading}
        search={searchQuery}
        onSearchChange={setSearchQuery}
        onOpenCandidate={overlay.openCandidate}
        addCandidate={
          <AddCandidateDialog
            triggerTone="primary"
            openFromUrl={Boolean(search.newCandidate)}
            onClearUrlOpen={() =>
              navigate({
                search: (prev: typeof search) => ({
                  ...prev,
                  newCandidate: undefined,
                }),
                replace: true,
              })
            }
          />
        }
      />

      <CandidateProfileOverlayContainer
        candidateId={overlay.candidateId}
        outreachJobId={
          overlay.candidateId
            ? (linkByCandidate.get(overlay.candidateId)?.jobId ?? null)
            : null
        }
        outreachJobTitle={
          overlay.candidateId
            ? linkByCandidate.get(overlay.candidateId)?.jobTitle
            : undefined
        }
        currentStage={
          overlay.candidateId
            ? (linkByCandidate.get(overlay.candidateId)?.stage ?? null)
            : null
        }
        onClose={overlay.closeOverlay}
        activeTab={overlay.tab}
        onTabChange={overlay.setTab}
        activeCommentMode={overlay.commentMode}
        activeCommentId={overlay.commentId ?? null}
        onCommentChange={overlay.setComment}
      />
    </>
  )
}
